import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { useTheme } from "../context/ThemeContext";
import API_BASE_URL from "../config";

const regions = [
  { label: "Oradea", value: 0 },
  { label: "Arad", value: 2000 },
  { label: "Leș", value: 4000 },
  { label: "Leș 2", value: 8000 },
];

const AddUserModal = ({ isOpen, onClose, onUserAdded }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("Agent");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [region, setRegion] = useState(0);
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const { darkMode } = useTheme();
  const [canAccessMarmurist, setCanAccessMarmurist] = useState(false);
  const [hasCommission, setHasCommission] = useState(false);
  const [commissionLimit, setCommissionLimit] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setEmail("");
      setPassword("");
      setRole("Agent");
      setRegion(0);
      setPhone("");
      setError(null);
      setLoading(false);
      setSuccessMessage("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    let valid = true;

    if (!name.trim()) {
      setNameError("Numele este obligatoriu!");
      valid = false;
    } else {
      setNameError("");
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.match(emailRegex)) {
      setEmailError("Introduceți o adresă de email validă!");
      valid = false;
    } else {
      setEmailError("");
    }

    if (password.length < 6) {
      setPasswordError("Parola trebuie să conțină cel puțin 6 caractere!");
      valid = false;
    } else {
      setPasswordError("");
    }

    if (!phone.match(/^\+?\d{7,15}$/)) {
      setPhoneError("Introduceți un număr de telefon valid!");
      valid = false;
    } else {
      setPhoneError("");
    }

    // 🔴 Ha van bármilyen hiba, NE küldjük be az űrlapot!
    if (!valid) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_BASE_URL}/api/auth/register-admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          email,
          password,
          role,
          phone,
          region,
          canAccessMarmurist,
          hasCommission,
          commissionLimit:
            commissionLimit === "" ? null : Number(commissionLimit),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Eroare la crearea utilizatorului."
        );
      }

      setSuccessMessage("Utilizator creat cu succes! ✅");
      setTimeout(() => {
        setSuccessMessage("");
        onUserAdded();
        onClose();
      }, 1000);
    } catch (err) {
      setSuccessMessage(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.id === "modal-overlay") {
      onClose();
    }
  };

  return (
    <div
      id="modal-overlay"
      className={`fixed inset-0 flex items-center justify-center ${
        darkMode ? "bg-black bg-opacity-80" : "bg-gray-300 bg-opacity-80"
      }`}
      onClick={handleOverlayClick}
    >
      <div
        className={`p-6 rounded-2xl shadow-xl w-[600px] min-h-[500px] relative ${
          darkMode ? "bg-gray-900 text-white" : "bg-white text-black"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className={`absolute top-2 right-3 ${
            darkMode ? "text-red-500" : "text-red-700"
          } text-2xl`}
        >
          ✖
        </button>

        <h2
          className={`text-2xl font-semibold mb-4 text-center ${
            darkMode ? "text-blue-400" : "text-blue-600"
          }`}
        >
          Adaugă utilizator nou
        </h2>

        {/* Success message */}
        {successMessage && (
          <p className="text-green-500 bg-green-900 p-2 rounded mb-3 text-center">
            {successMessage}
          </p>
        )}

        {/* Error message */}
        {error && (
          <p className="text-red-500 bg-red-900 p-2 rounded mb-3 text-center">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          {/* Name Input */}
          <div>
            <label
              className={`block ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } font-semibold mb-1`}
            >
              Nume<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="username"
              placeholder="Introduceți numele"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError("");
              }}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
              }`}
              autoComplete="off"
            />
            {nameError && (
              <p className="text-red-400 text-sm mt-1">{nameError}</p>
            )}
          </div>

          {/* Email Input */}
          <div>
            <label
              className={`block ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } font-semibold mb-1`}
            >
              Email<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="useremail"
              placeholder="Introduceți email-ul"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
              }`}
              autoComplete="off"
            />
            {emailError && (
              <p className="text-red-400 text-sm mt-1">{emailError}</p>
            )}
          </div>

          {/* Password Input */}
          <div>
            <label
              className={`block ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } font-semibold mb-1`}
            >
              Parolă<span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              name="new-password"
              placeholder="Introduceți parola"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError("");
              }}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
              }`}
              autoComplete="new-password"
            />
            {passwordError && (
              <p className="text-red-400 text-sm mt-1">{passwordError}</p>
            )}
          </div>

          {/* Role Dropdown */}
          <div>
            <label
              className={`block ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } font-semibold mb-1`}
            >
              Rol
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
              }`}
            >
              <option value="Agent">Agent</option>
              <option value="Admin">Admin</option>
            </select>
          </div>

          {/* Regiune */}
          <div>
            <label
              className={`block ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } font-semibold mb-1`}
            >
              Regiune
            </label>
            <select
              value={region}
              onChange={(e) => setRegion(Number(e.target.value))}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
              }`}
            >
              {regions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Telefon */}
          <div>
            <label
              className={`block ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } font-semibold mb-1`}
            >
              Nr. Telefon<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Introduceți numărul de telefon"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneError("");
              }}
              className={`w-full p-2 border rounded-2xl ${
                darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
              }`}
            />
            {phoneError && (
              <p className="text-red-400 text-sm mt-1">{phoneError}</p>
            )}
          </div>

          <div className="col-span-2 grid grid-cols-2 gap-4 mt-4">
            {/* Acces la clienți marmurist */}
            <label
              htmlFor="marmurist"
              className="flex items-center space-x-2 justify-center cursor-pointer"
            >
              <input
                id="marmurist"
                type="checkbox"
                checked={canAccessMarmurist}
                onChange={(e) => setCanAccessMarmurist(e.target.checked)}
                className="w-6 h-6"
              />
              <span
                className={`${
                  darkMode ? "text-gray-300" : "text-gray-700"
                } font-semibold`}
              >
                Acces la clienți marmurist
              </span>
            </label>

            {/* Eligibil pentru comision */}
            <label
              htmlFor="commission"
              className="flex items-center space-x-2 justify-center cursor-pointer"
            >
              <input
                id="commission"
                type="checkbox"
                checked={hasCommission}
                onChange={(e) => setHasCommission(e.target.checked)}
                className="w-6 h-6"
              />
              <span
                className={`${
                  darkMode ? "text-gray-300" : "text-gray-700"
                } font-semibold`}
              >
                Eligibil pentru comision
              </span>
            </label>
          </div>

          {hasCommission && (
            <div className="col-span-2">
              <label
                className={`block ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                } font-semibold mb-1`}
              >
                Limită comision (€)
              </label>
              <input
                type="number"
                min={0}
                step={100}
                value={commissionLimit}
                onChange={(e) => setCommissionLimit(e.target.value)}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "bg-gray-800 text-white border-gray-600"
                    : "bg-white text-black border-gray-400"
                }`}
                placeholder="Ex: 10000"
              />
            </div>
          )}

          {/* Gombok */}
          <div className="col-span-2 flex justify-between gap-4 mt-4">
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: darkMode ? "#1976d2" : "#1565c0",
                borderRadius: "30px",
                color: "white",
                fontSize: "16px",
                padding: "10px",
                height: "56px",
                flex: 1,
                "&:hover": { bgcolor: darkMode ? "#1565c0" : "#1976d2" },
              }}
              disabled={loading}
            >
              {loading ? "Se încarcă..." : "Adaugă utilizator"}
            </Button>

            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                color: darkMode ? "#fff" : "#000",
                borderColor: darkMode ? "#fff" : "#000",
                borderRadius: "30px",
                fontSize: "1rem",
                padding: "10px",
                height: "56px",
                flex: 1,
                "&:hover": { borderColor: darkMode ? "#ccc" : "#bbb" },
              }}
            >
              Închide
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;
